import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { can } from '../../utils/permissions';
import { formatLocalMoment } from '../../utils/specimenCode';
import { useAuth } from '../../context/AuthContext';
import QRCodeLabel from '../../components/QRCodeLabel';
import LabelBatchGenerator from '../../components/specimens/LabelBatchGenerator';
import SpecimenOnSite from '../../components/specimens/SpecimenOnSite';
import SpecimenRegisterForm from '../../components/specimens/SpecimenRegisterForm';
import * as S from '../../components/specimens/styles';

/*
 * Specimens: every specimen's code from the field to the freezer.
 *
 * The list, plus the three ways a code is born -- here and now on a phone,
 * after the fact from a notebook or an existing collection, or printed ahead
 * of a field trip. Each opens on the specimen's own page, where its storage,
 * its workflow graph and its history live (SpecimenDetail).
 */
const PAGE_SIZE = 25;
const STATUSES = ['', 'active', 'reserved', 'archived', 'void'];
const PANELS = ['onSite', 'register', 'labels'];
// The Isolate ID (the ISO code) and the short lab code lead, as on every
// isolate table; the QR beside them encodes the Isolate ID.
const COLUMNS = ['isolateId', 'labCode', 'qr', 'status', 'collected', 'taxon', 'location', 'storage'];

export default function Specimens() {
  const { t } = useTranslation(['specimens', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canRegister = can(user, 'specimen:register');
  const [panel, setPanel] = useState(() => (PANELS.includes(location.state?.panel) ? location.state.panel : ''));
  const [filters, setFilters] = useState({ q: '', status: '', batch_id: '' });
  const [query, setQuery] = useState({ q: '', status: '', batch_id: '' });
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    Object.entries(query).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get('/specimens', { params })
      .then((res) => { setData(res.data); setError(''); })
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, [query, page]);

  useEffect(() => { load(); }, [load]);

  const search = (e) => {
    e.preventDefault();
    setPage(0);
    setQuery({ ...filters, q: filters.q.trim() });
  };
  const open = (id) => navigate(`/specimens/${id}`);
  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <h2 style={{ margin: '0 0 4px' }}>{t('title')}</h2>
      <p style={{ margin: 0, color: 'var(--c-text-muted-fg)', fontSize: 14, maxWidth: 720 }}>{t('intro')}</p>

      {canRegister && (
        <div role="tablist" aria-label={t('actions')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          {PANELS.map((key) => (
            <button key={key} type="button" role="tab" aria-selected={panel === key}
              onClick={() => setPanel(panel === key ? '' : key)} data-testid={`panel-${key}`}
              title={t(`panels.tip.${key}`)}
              style={panel === key ? S.btn() : S.ghostBtn}>{t(`panels.${key}`)}</button>
          ))}
        </div>
      )}
      {panel === 'onSite' && <SpecimenOnSite t={t} onOpen={open} />}
      {panel === 'register' && <SpecimenRegisterForm t={t} onCreated={(s) => open(s.specimen_id)} onOpen={open} />}
      {panel === 'labels' && (
        <LabelBatchGenerator t={t} onReserved={(b) => {
          setFilters((f) => ({ ...f, batch_id: b.batch_id })); setPage(0);
          setQuery((q) => ({ ...q, batch_id: b.batch_id }));
        }} />
      )}

      <form onSubmit={search} style={{ ...S.card, ...S.row }} role="search">
        <label style={{ ...S.label, flex: '3 1 240px' }}>
          {t('list.search')}
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder={t('list.searchPlaceholder')} style={S.input} title={t('list.tip.search')}
            data-testid="specimen-search" />
        </label>
        <label style={{ ...S.label, flex: '1 1 150px' }}>
          {t('list.status')}
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            style={S.input} title={t('list.tip.status')} data-testid="specimen-status-filter">
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s ? t(`status.${s}`) : t('list.anyStatus')}</option>)}
          </select>
        </label>
        <button type="submit" style={S.btn()} title={t('list.tip.apply')}>{t('list.apply')}</button>
        {query.batch_id && (
          <button type="button" style={S.ghostBtn} title={t('list.tip.clearBatch')}
            onClick={() => { setFilters((f) => ({ ...f, batch_id: '' })); setQuery((q) => ({ ...q, batch_id: '' })); }}>
            {t('list.clearBatch')}
          </button>
        )}
      </form>

      {error && <p style={S.error} role="alert">{error}</p>}
      <div style={{ ...S.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }} data-testid="specimen-table">
          <thead>
            <tr style={{ background: 'var(--c-bg-muted-2)', textAlign: 'start' }}>
              {COLUMNS.map((col) => (
                <th key={col} style={th}>{t(`list.columns.${col}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.specimen_id} onClick={() => open(s.specimen_id)} data-testid={`specimen-row-${s.specimen_id}`}
                tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') open(s.specimen_id); }}
                style={{ borderTop: '1px solid var(--c-border)', cursor: 'pointer' }} title={t('list.tip.row')}>
                <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <bdi dir="ltr" style={S.mono}>{s.specimen_code}</bdi>
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }} data-testid="specimen-lab-code">
                  {s.lab_code ? <bdi dir="ltr" style={S.mono}>{s.lab_code}</bdi> : '—'}
                </td>
                <td style={td}><QRCodeLabel value={s.specimen_code} size={40} compact /></td>
                <td style={td}><span style={S.pill(s.status)}>{t(`status.${s.status}`)}</span></td>
                <td style={td}>{formatLocalMoment(s.collected_at_local, s.time_known) || '—'}</td>
                <td style={{ ...td, fontStyle: s.taxon_name ? 'italic' : 'normal' }}>{s.taxon_name || '—'}</td>
                <td style={td}>{s.location_name || s.location_code}</td>
                <td style={td}>
                  {s.preservation_method ? t(`storage.methods.${s.preservation_method}`) : '—'}
                  {s.copies_count > 0 && <span style={S.hint}> · {t('storage.copies', { count: s.copies_count })}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && data.items.length === 0 && (
          <p style={{ ...S.hint, padding: 16 }} data-testid="specimen-empty">{t('list.empty')}</p>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
        <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={S.ghostBtn}
          title={t('list.tip.previous')}>{t('list.previous')}</button>
        <span style={{ fontSize: 13 }}>{t('list.page', { page: page + 1, pages, total: data.total })}</span>
        <button type="button" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} style={S.ghostBtn}
          title={t('list.tip.next')}>{t('list.next')}</button>
      </div>
    </div>
  );
}

const th = { padding: '8px 10px', fontSize: 13, fontWeight: 600, textAlign: 'start' };
const td = { padding: '8px 10px', fontSize: 14, verticalAlign: 'middle' };
